/**
 * Where a rigid body comes from, and what the run remembers about it — #84.
 *
 * `sim/rigid.ts` owns the mechanism: a crate falls, tips, sleeps, burns and
 * structurally fails. This file is the other half — the one #42 deliberately
 * did not ship — and it is the answer to a design question rather than a piece
 * of wiring, so the argument is written out before the code.
 *
 * ==== THE QUESTION ======================================================
 *
 * `world/supportSites.ts` registers inside chunk generation and survives
 * eviction for free, because a support group is keyed `"<cx>,<cy>:<situation>"`
 * and a regenerated chunk re-registers *over* its own entry. The group has no
 * state worth preserving: it is a description of cells the seed will rebuild
 * identically.
 *
 * **A body is not like that.** It has a position it has rolled to, a rotation
 * it tipped into, and a pixel array that has been burned and dug since it was
 * placed. Chunks are evicted past the 256-resident cap and regenerate from the
 * seed, so a generator that simply called `spawn` would give a chunk walked out
 * of and back into either *two* crates or *one crate the player had already
 * broken*. Both are worse than having no crates at all, which is why #42 left
 * `spawn` with no callers and said so.
 *
 * ==== THE THREE OPTIONS, AND THE ONE TAKEN ==============================
 *
 * #84 lists three. In short:
 *
 * 1. **Chunk-owned, reset on regeneration.** Simplest, matches supports
 *    exactly. A crate you broke comes back when you walk two screens away —
 *    which contradicts the title screen's own promise that the world remembers
 *    what you did to it.
 * 2. **Run-owned, persisting outside the chunk cache**, with generation only
 *    *seeding* the first time a chunk is generated in a run.
 * 3. **Baked back into terrain on eviction and re-lifted on regeneration.**
 *    Cheapest memory, but a mid-air crate lands the instant its chunk is
 *    evicted, which is a teleport if the player is watching from a chunk away.
 *
 * **This is option 2**, and it is implemented in the shape this codebase
 * already has a doctrine for, which makes it much less work than #84 feared.
 * docs/01 §8: *the run carries its seed plus what the player did to the world,
 * and the world is re-derived.* `VendorLedger` says the same thing about
 * vendors — "pure in `(seed, band, index)` and therefore rebuilt, not saved;
 * what cannot be re-derived is what the player took off the shelf, so that is
 * the only thing recorded."
 *
 * **#42's doors landed in this file too, and they are two things it did not
 * have to grow a second copy of.** A door is a body, so the ledger, the ring,
 * the retire/restore round trip and the `gone` flag are all reused unchanged;
 * the only thing that had to become plural is the site list, which is now
 * `[x, y, shape]` triples so a chunk can draw both. What a door *does* need
 * that a crate does not is a carved doorway — a crate belongs anywhere there is
 * a floor and a hatch is not a hatch without a hole under it — so `doorSiteFor`
 * stamps where `crateSiteFor` only reads. That is `supportSites.ts`'s pattern
 * and it keeps `supportSites.ts`'s guarantee: the stamp turns rock into air and
 * never the other way round.
 *
 * So a crate is two things that are kept apart:
 *
 * - **Where crates are** is pure in `(seed, cx, cy)` and in the generated
 *   chunk's own cells. `crateSiteFor` below is that function. It is re-derived
 *   every time the chunk is generated and it never changes, so it costs no
 *   memory and cannot go stale.
 * - **What happened to a crate** is a run-owned diff — gone, or moved, or
 *   chewed — held by `RunBodies` and written straight through to
 *   `RunState.bodies`. A crate nobody has touched has **no record at all**,
 *   exactly as an undug world has an empty terrain diff log.
 *
 * #84 asks for a "first-generation marker per chunk". Nothing in the codebase
 * records that (`WorldGen.anchorIndex` is the closest thing and is a dedupe on
 * derived data, not a visit log), and this design does not need one: the
 * *ledger entry* is the marker. Absent means "as the seed authored it", which
 * is the right thing to instantiate whether the chunk has been generated once
 * or fifty times.
 *
 * ==== WHAT ACTUALLY HAPPENS ON EVICTION =================================
 *
 * A live body is not allowed to outlive the chunk it stands in. `sync` runs
 * once a tick and keeps bodies instantiated in exactly the chunks
 * `CellWorld.updateActive` keeps generated around the view — ring 0 (on screen)
 * plus `BODY_RING`. Leaving that ring **captures** the body into the ledger and
 * retires it; entering it **restores** the body from the ledger, or seeds it
 * from the site if there is no record.
 *
 * That makes the retire/restore path the *common* path — it runs whenever you
 * walk a chunk and a half away — rather than a rare 256-chunk event that would
 * be exercised once an hour and never in a fixture. Eviction proper is then
 * just the case where the chunk went away first, and the only thing it changes
 * is that the body's cells must **not** be cleared out of the grid, because
 * they left with the chunk and clearing them would generate the chunk again and
 * carve the crate's silhouette out of fresh rock. That is the `clearCells`
 * argument to `RigidBodies.retire`, and it is the whole difference.
 *
 * ==== WHAT A RESUMED RUN DOES ABOUT CRATES ==============================
 *
 * `RunState.bodies` is a flat number array, encoded the way `TerrainDiffLog`
 * is, and `serializeRun` carries it through its `...rest` spread with no change
 * to `save.ts` and no `SAVE_VERSION` bump. A v4 save has no such field; that
 * decodes as an empty ledger, which is the honest reading — a run saved before
 * this landed had no crates in it.
 *
 * So, in the terms of `runState.ts`'s resume table: **a resumed run's crates
 * are back where the seed put them, except the ones the run recorded a diff
 * for. A crate you broke is still broken. A crate you pushed is where you
 * pushed it, with the damage you did to it.** What does not come back is cell
 * state — a crate that was on fire is not on fire, for the same reason the
 * cave's fires are out (see `RigidBodies.capture`).
 *
 * ==== COST, AND WHY SEEDING IS RATIONED =================================
 *
 * Per chunk generated: `crateSiteFor` is bounded at `PROBE_TRIES × (MAX_DESCENT
 * + CRATE_SPAN + CRATE_SPAN²)` = 1,840 array reads. **Measured at 1.8 µs
 * against the shipping generator's 3,251 µs per chunk — 0.06%** (bench box,
 * best of 5×40 chunks, `tests/bodySites.test.ts`). It is timed on its own
 * rather than as a decorated-versus-bare difference, because at three orders of
 * magnitude below generation that difference is entirely noise and came out
 * *negative* more often than not.
 *
 * #42's hatch scan is dearer, and gets its own figure: **10.0 µs against 3,190
 * µs per chunk — 0.31%** (`tests/rigidDoor.test.ts`), on the half of chunks the
 * `DOOR_PERIOD` gate lets through. It costs thirty times a crate's because
 * before it can reject a site it has to read the surface of all twenty-six
 * columns, which is the price of levelling — and levelling is the difference
 * between hatches existing and hatches existing only in the test fixture.
 *
 * Per tick: `sync` walks the ring (49 chunk-key lookups at the default view)
 * and the live list (never more than `MAX_BODIES`). Instantiation is capped at
 * `MAX_SEEDS_PER_TICK` because placing a sleeping body charges the rigid
 * solver's work counter — `cells + 4 × edge`, 239 units for the crate — and
 * `RigidBodies.maxWorkPerTick` is 3528. Two per tick is 478 units, which sits
 * beside the 1172 that four crates falling actually spend. A count, not a
 * clock: docs/01 §9's rule, and the reason the ring fills over ~8 ticks instead
 * of in one spike.
 */

import { profiler } from '../core/profiler';
import { hash2 } from '../core/rng';
import { M, VOID } from '../sim/materials';
import { SHAPE_CRATE, SHAPE_DOOR, shapes } from '../sim/rigidShapes';
import { MAX_BODIES, type BodyCapture, type DoorAxis, type RigidBodies } from '../sim/rigid';
import { CHUNK_SHIFT, CHUNK_SIZE, type CellWorld, type Chunk, type ChunkGenerator } from '../sim/world';

/**
 * One chunk in this many draws a crate candidate.
 *
 * Derived, in the same way `SITE_PERIOD` is. `sync` keeps bodies live across
 * ring 0 plus `BODY_RING`, which at the shipping view is 7×7 = 49 chunks, and
 * `MAX_BODIES` is 32. 49/3 is 16, so a fully populated ring uses half the
 * registry and leaves room for the crates the player has pushed out of it. A
 * period of 1 would want 49 bodies and refuse 17 of them, and a refused crate
 * is a crate that is visible on one visit and absent on the next.
 *
 * Not every candidate finds a floor with room above it, so the density in play
 * is lower than one in three — measured at about one crate per five chunks
 * across the shipping bands.
 */
export const CRATE_PERIOD = 3;

/**
 * Rings of chunks beyond the visible span that keep their bodies instantiated.
 *
 * 1, which is exactly the ring `CellWorld.updateActive` guarantees generated.
 * A body therefore exists wherever the player can see and one whole chunk
 * further, so the retire that removes its cells from the grid happens 128 cells
 * off the edge of the screen and cannot be watched.
 */
export const BODY_RING = 1;

/** Bodies instantiated per tick. See the cost note in the header. */
export const MAX_SEEDS_PER_TICK = 2;

/**
 * Placements attempted inside a chunk before giving up.
 *
 * A site is a candidate, not a guarantee: a chunk of solid rock or open sky has
 * nowhere to stand a crate and forcing one would mean carving geometry the
 * generator did not intend. Eight rather than `supportSites.ts`'s twenty-four
 * because each try here *descends to the floor* instead of demanding that the
 * probe land on one — see `MAX_DESCENT` — so a try is worth several of those.
 */
const PROBE_TRIES = 8;

/**
 * Rows a probe will fall before giving up on its column.
 *
 * The probe picks a point and looks for the ground under it, which is the
 * difference between a placement that usually works and one that usually does
 * not: demanding that a randomly chosen row *be* the floor succeeds about one
 * time in fourteen against a chunk with floors every fourteen rows, and eight
 * such tries find a site in barely half of them. Descending finds one on the
 * first try in the same chunk. 48 bounds the cost — worst case per chunk is
 * `PROBE_TRIES × (MAX_DESCENT + CRATE_SPAN + CRATE_SPAN²)` = 1,840 array reads
 * against the 16,384 cells the generator has just written with multi-octave
 * noise.
 */
const MAX_DESCENT = 48;

/** Half-width of the crate, in cells. `crateSpec` is 13×13. */
const CRATE_HALF = 6;

/** Free rows the crate needs above the floor row it stands on. */
const CRATE_SPAN = CRATE_HALF * 2 + 1;

function solidAt(chunk: Chunk, x: number, y: number): boolean {
  return M.solid[chunk.mat[(y << CHUNK_SHIFT) | x]] === 1;
}

/**
 * Where this chunk's crate stands, in world cells, or null.
 *
 * Pure in `(seed, cx, cy)` and in the chunk's own generated cells — nothing
 * here reads a neighbour, a clock, an `Rng`, or generation order, so a chunk
 * regenerated after eviction answers identically. That is what lets the ledger
 * key on `(cx, cy, index)` and store nothing for a crate nobody has touched.
 *
 * The returned coordinate is the body's **origin**, which for an odd-sized
 * shape is its centre cell: a crate resting on floor row `fy` centres on
 * `fy - CRATE_HALF - 1`.
 */
export function crateSiteFor(
  chunk: Chunk,
  cx: number,
  cy: number,
  seed: number,
): { x: number; y: number } | null {
  if (hash2(cx, cy, seed ^ 0x1d3c5b77) % CRATE_PERIOD !== 0) return null;

  const xRange = CHUNK_SIZE - CRATE_SPAN - 1;
  const yRange = CHUNK_SIZE - CRATE_SPAN - 2;
  if (xRange < 1 || yRange < 1) return null;

  for (let t = 0; t < PROBE_TRIES; t++) {
    const h = hash2(cx * 0x51ed + t, cy * 0x2c9f + t, seed ^ 0x6b43a917);
    const x0 = 1 + (h % xRange);
    const y0 = CRATE_SPAN + 1 + ((h >>> 11) % yRange);

    // Fall to the ground under the probe, along the crate's centre column.
    let fy = -1;
    for (let k = 0; k < MAX_DESCENT; k++) {
      const y = y0 + k;
      if (y >= CHUNK_SIZE) break;
      if (solidAt(chunk, x0 + CRATE_HALF, y)) {
        fy = y;
        break;
      }
    }
    if (fy < CRATE_SPAN + 1) continue;

    // The floor must carry the whole width — 13 reads, and it rejects far more
    // often than the 169-cell clearance scan below, so it goes first.
    let ok = true;
    for (let x = x0; x < x0 + CRATE_SPAN && ok; x++) ok = solidAt(chunk, x, fy);
    if (!ok) continue;
    for (let d = 1; d <= CRATE_SPAN && ok; d++) {
      for (let x = x0; x < x0 + CRATE_SPAN && ok; x++) {
        ok = chunk.mat[((fy - d) << CHUNK_SHIFT) | x] === VOID;
      }
    }
    if (!ok) continue;

    // The origin of an odd-sized shape is its centre cell, so a crate whose
    // bottom row rests on `fy - 1` centres `CRATE_HALF + 1` above the floor.
    return {
      x: (cx << CHUNK_SHIFT) + x0 + CRATE_HALF,
      y: (cy << CHUNK_SHIFT) + fy - CRATE_HALF - 1,
    };
  }
  return null;
}

// ----------------------------------------------------------------- the hatch

/**
 * One chunk in this many is *scanned* for a hatch.
 *
 * **Not the rationing, unlike `CRATE_PERIOD` — the geometry is.** A crate needs
 * a floor, which every chunk has, so its period is what decides how many
 * crates there are. A hatch needs a sill it can be levelled into with open air
 * above it *and* a drop below it, which is two stacked voids separated by a
 * thin slab, and the shipping generator offers that in **about 3.6% of chunks**
 * when every chunk is scanned (measured across three seeds in
 * `tests/rigidDoor.test.ts`). Halving that with the gate gives roughly **one
 * hatch per 55 chunks** — about one per 49-chunk body ring, which is the
 * density `MAX_AWAKE` wants, since every swing is an awake body.
 *
 * The period is therefore two and not seven: at seven the geometry and the gate
 * would compound to one hatch per two hundred chunks, and a feature you meet
 * twice a run is a feature nobody tests against. What the gate buys at two is
 * halving the scan cost, which is the honest reason to keep it at all.
 */
export const DOOR_PERIOD = 2;

/** Half-extents of the 13×5 plate. `doorSpec` is the authority. */
const DOOR_HALF_W = 6;
const DOOR_HALF_H = 2;
const DOOR_W = DOOR_HALF_W * 2 + 1;
const DOOR_H = DOOR_HALF_H * 2 + 1;

/**
 * Cells from shut to open, and why it is exactly the plate's own width.
 *
 * The recess is carved beside the hole and is the same width as the plate, so
 * a travel shorter than `DOOR_W` would leave the hatch overhanging the hole
 * when open — a hatch that never fully opens — and a longer one would drive it
 * into rock and jam every time. One number, fixed by the geometry, not tuned.
 */
export const DOOR_TRAVEL = DOOR_W;

/** Columns a hatch site occupies: the recess, then the hole. */
const DOOR_SPAN = DOOR_TRAVEL + DOOR_W;

/** Clear rows the hatch needs above it, so something can stand on it. */
const DOOR_HEADROOM = 10;

/** Clear rows the hole needs below it, so opening it goes somewhere. */
const DOOR_DROP = 10;

/**
 * Rows of unevenness the sill may have, and be levelled.
 *
 * **This is the constant that decides whether hatches exist at all**, and it
 * was found by measuring rather than chosen. The first version demanded a floor
 * already flat across all twenty-six columns and found **zero** sites in 144
 * chunks around spawn on each of three seeds. Instrumenting the rejections said
 * why, and it was not the slab or the drop: of ninety-six candidates that got
 * past everything else, **eighty-six had zero clear rows above the hole**,
 * because the descent measures one column and the other twelve were solid a row
 * higher. Floors in a noise world are not flat, and a plate that slides along a
 * fixed row needs them to be.
 *
 * So the site levels its own sill: every column is cut down to the *deepest* of
 * the twenty-six surfaces. That is rock becoming air, which is the direction
 * `supportSites.ts`'s connectivity guarantee permits, and it is what a doorway
 * is — an authored flat threshold. Eight rows is where the conversion rate
 * stops improving (2.1% at 13 headroom, 3.6% at 10, and 12 rows of levelling
 * buys nothing over 8) and it bounds the carve at 26 × 8 cells.
 */
const DOOR_LEVEL = 8;

/** Rows of floor the recess is cut into. `DOOR_H` plus a solid row beneath. */
const SLAB_MIN = DOOR_H + 1;

/** Rows a slab may be and still be punched through. Bounds the scan. */
const SLAB_MAX = 20;

/** Placements attempted per chunk. */
const DOOR_TRIES = 8;

/**
 * The axis every hatch is on: thirteen cells to the **left** of shut.
 *
 * A function rather than a literal because `RunBodies` has to hand the same
 * segment to `spawnDoor` and to `restore`, and two copies of a constraint is
 * how a restored door ends up on a different rail from the one the world was
 * carved for. Left rather than a hashed side because the stamp below carves
 * the recess on the left; the two are one decision and are written once.
 */
export function doorAxisAt(x: number, y: number): DoorAxis {
  // The sensed volume sits one whole plate-depth above the shut plate: what
  // opens a hatch is weight *on* it, and what falls *through* it is a crush
  // victim. `DoorAxis.senseDy` argues that separation at length; here it is
  // simply `DOOR_H`, because the plate is `DOOR_H` deep and the two volumes
  // must not touch.
  return {
    dx: -1, dy: 0, travel: DOOR_TRAVEL,
    shutX: x, shutY: y,
    senseDx: 0, senseDy: -DOOR_H,
  };
}

/**
 * Where this chunk's hatch is, in world cells, **and the hole it needs**.
 *
 * Unlike `crateSiteFor` this one *stamps*, and the argument for that is
 * `supportSites.ts`'s: a chunk regenerates from the seed, so anything carved
 * after generation is lost the moment the player walks two screens away, and
 * carving inside the generator is the only arrangement in which "pure in the
 * seed" is true. A crate needs nothing carved because a crate belongs anywhere
 * there is a floor. **A hatch is not a hatch unless there is a hole under it**,
 * and a hole is geometry.
 *
 * It keeps `supportSites.ts`'s one connectivity guarantee, which is the reason
 * that file states it as a guarantee rather than as a hope: **this stamp never
 * turns air into matter.** Rock becomes air in the hole and in the recess and
 * nowhere else. The hatch's own wood does land in what is now air — but the
 * solver puts it there, at the top of a hole that was solid rock a moment ago,
 * so no route that existed before the stamp is narrowed by it and
 * `reachability.test.ts` cannot be made worse.
 *
 * The returned coordinate is the plate's **shut** origin: the centre cell of
 * the hole's top five rows.
 */
export function doorSiteFor(
  chunk: Chunk,
  cx: number,
  cy: number,
  seed: number,
): { x: number; y: number } | null {
  if (hash2(cx, cy, seed ^ 0x4f1bd2a3) % DOOR_PERIOD !== 0) return null;

  const xRange = CHUNK_SIZE - DOOR_SPAN - 2;
  const yRange = CHUNK_SIZE - DOOR_HEADROOM - DOOR_LEVEL - SLAB_MAX - DOOR_DROP - 2;
  if (xRange < 1 || yRange < 1) return null;

  for (let t = 0; t < DOOR_TRIES; t++) {
    const h = hash2(cx * 0x2f0d + t, cy * 0x71c3 + t, seed ^ 0x1a77e5b9);
    const x0 = 1 + (h % xRange);
    const y0 = DOOR_HEADROOM + DOOR_LEVEL + 1 + ((h >>> 11) % yRange);
    const holeX = x0 + DOOR_TRAVEL;
    const holeMid = holeX + DOOR_HALF_W;

    // Fall to the first solid row under the probe, down the hole's centre.
    // This only *finds* the neighbourhood of the floor; the sill's real row is
    // decided below, from all twenty-six columns.
    let fy0 = -1;
    for (let k = 0; k < MAX_DESCENT; k++) {
      const y = y0 + k;
      if (y + SLAB_MAX + DOOR_DROP >= CHUNK_SIZE) break;
      if (solidAt(chunk, holeMid, y)) {
        fy0 = y;
        break;
      }
    }
    if (fy0 < DOOR_HEADROOM + DOOR_LEVEL) continue;

    // Each column's own surface, within `DOOR_LEVEL` of the probe's. A column
    // whose surface is outside that band makes this site too uneven to level,
    // and it is rejected rather than carved deeper — the bound is what keeps
    // the stamp from quarrying a pit out of a hillside.
    const top = SILL;
    let bad = false;
    for (let i = 0; i < DOOR_SPAN && !bad; i++) {
      let s = -1;
      for (let d = -DOOR_LEVEL; d <= DOOR_LEVEL; d++) {
        if (solidAt(chunk, x0 + i, fy0 + d)) {
          s = fy0 + d;
          break;
        }
      }
      if (s < 0) bad = true;
      else top[i] = s;
    }
    if (bad) continue;

    // The sill is the **deepest** of the surfaces, so levelling only ever
    // removes rock. Taking the shallowest instead would mean filling air.
    let fy = top[0];
    for (let i = 1; i < DOOR_SPAN; i++) if (top[i] > fy) fy = top[i];

    // Cheapest rejection first: the levelled sill must carry the recess *and*
    // the hole across its full span, or the hatch is a plate lying on nothing.
    let ok = true;
    for (let d = 0; d < SLAB_MIN && ok; d++) {
      for (let x = x0; x < x0 + DOOR_SPAN && ok; x++) ok = solidAt(chunk, x, fy + d);
    }
    if (!ok) continue;

    // How thick the slab is, measured rather than assumed: a hole has to go all
    // the way through or it is an alcove.
    let by = -1;
    for (let d = SLAB_MIN; d <= SLAB_MAX; d++) {
      if (!solidAt(chunk, holeMid, fy + d)) {
        by = fy + d;
        break;
      }
    }
    if (by < 0) continue;
    for (let y = fy; y < by && ok; y++) {
      for (let x = holeX; x < holeX + DOOR_W && ok; x++) ok = solidAt(chunk, x, y);
    }
    if (!ok) continue;

    // Air above each column's *own* surface, back to the levelled headroom. The
    // rows between a column's surface and the sill are about to become air, so
    // they are not required to be air now — that is the whole point of levelling
    // and it is why this loop starts at `top[i]` rather than at `fy`.
    for (let i = 0; i < DOOR_W && ok; i++) {
      const x = holeX + i;
      for (let y = fy - DOOR_HEADROOM; y < top[DOOR_TRAVEL + i] && ok; y++) {
        ok = chunk.mat[(y << CHUNK_SHIFT) | x] === VOID;
      }
    }
    if (!ok) continue;
    // …and air below it, so opening it drops them somewhere. A trapdoor onto
    // solid rock is a decoration.
    for (let d = 0; d < DOOR_DROP && ok; d++) {
      for (let x = holeX; x < holeX + DOOR_W && ok; x++) {
        ok = chunk.mat[((by + d) << CHUNK_SHIFT) | x] === VOID;
      }
    }
    if (!ok) continue;

    // Level, then punch, then slot. All three are rock becoming air.
    for (let i = 0; i < DOOR_SPAN; i++) carve(chunk, x0 + i, x0 + i + 1, top[i], fy);
    carve(chunk, holeX, holeX + DOOR_W, fy, by);
    carve(chunk, x0, x0 + DOOR_TRAVEL, fy, fy + DOOR_H);
    return {
      x: (cx << CHUNK_SHIFT) + holeMid,
      y: (cy << CHUNK_SHIFT) + fy + DOOR_HALF_H,
    };
  }
  return null;
}

/**
 * Scratch for the per-column surface scan. Module-level because `doorSiteFor`
 * runs inside chunk generation, which the whole file promises not to allocate
 * in, and because it is re-filled from scratch on every try.
 */
const SILL = new Int32Array(DOOR_SPAN);

/** Rock to air over a half-open rectangle. The only write this file makes. */
function carve(chunk: Chunk, x0: number, x1: number, y0: number, y1: number): void {
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const i = (y << CHUNK_SHIFT) | x;
      chunk.mat[i] = VOID;
      chunk.temp[i] = 20;
      chunk.flags[i] = 0;
      chunk.aux[i] = 0;
    }
  }
}

/**
 * Wrap a chunk generator so every chunk it builds reports its hatch, carving
 * the hole and the recess as it goes.
 *
 * Wrapped **inside** `withCrateSites`, so the crate's clearance scan runs after
 * the hole exists and cannot stand a crate on a hatch — a crate over a trapdoor
 * would be a fine piece of content and an appalling first one, because the
 * crate falls through the moment the player steps on the plate and the two
 * bodies are then both awake, both moving, and half the upload budget.
 */
export function withDoorSites(
  base: ChunkGenerator,
  seed: number,
  sink: BodySiteSink,
): ChunkGenerator {
  return (chunk: Chunk, cx: number, cy: number): void => {
    base(chunk, cx, cy);
    const site = doorSiteFor(chunk, cx, cy, seed);
    if (site !== null) sink.noteSite(cx, cy, site.x, site.y, SHAPE_DOOR);
  };
}

/**
 * Wrap a chunk generator so every chunk it builds reports its body sites.
 *
 * Reporting, not spawning. Generation happens under `getChunk`, which can fire
 * mid-tick from any cell write, and a `spawn` there would put a body into the
 * grid at a point in the frame order where `lift` has already run. The sink
 * records the pose; `RunBodies.sync` decides, once a tick and in one place,
 * whether anything should be standing there.
 *
 * Wrapped **outside** `withSupportSites`, so the clearance scan sees the
 * support site's stamped load and cannot put a crate inside a lava pocket.
 */
export function withCrateSites(
  base: ChunkGenerator,
  seed: number,
  sink: BodySiteSink,
): ChunkGenerator {
  return (chunk: Chunk, cx: number, cy: number): void => {
    base(chunk, cx, cy);
    const site = crateSiteFor(chunk, cx, cy, seed);
    if (site !== null) sink.noteSite(cx, cy, site.x, site.y, SHAPE_CRATE);
  };
}

/** What the site decorators report to. `RunBodies` is the only implementation. */
export interface BodySiteSink {
  noteSite(cx: number, cy: number, x: number, y: number, shape: number): void;
}

/**
 * `(cx, cy)` as one integer, by multiplication rather than by shifting.
 *
 * `world.ts`'s `chunkKey` uses `<< 16`, which overflows int32 for every
 * non-negative `cx` and comes back negative. That is harmless there because the
 * value is only ever a `Map` key and the collapse is consistent. It is not
 * harmless here: two chunks 32768 apart would share a key. 2^32 × 16 is exact
 * in a double, so the arithmetic form costs nothing and cannot collide.
 */
function chunkPack(cx: number, cy: number): number {
  return (cx + 32768) * 65536 + (cy + 32768);
}

/** `(cx, cy, index)` as one integer. ±32768 chunks, 16 sites per chunk. */
function siteKey(cx: number, cy: number, index: number): number {
  return chunkPack(cx, cy) * 16 + index;
}

interface Record_ {
  cx: number;
  cy: number;
  index: number;
  /** Broke, structurally failed, or burned through. Never comes back. */
  gone: boolean;
  /** Null for a `gone` record. */
  cap: BodyCapture | null;
}

/** An instantiated site. Carries its own chunk so nothing has to unpack a key. */
interface Live {
  id: number;
  cx: number;
  cy: number;
  index: number;
}

/** Fixed head of a flat record, before the variable-length mask. */
const REC_HEADER = 8;

/** Word `w` of a liveness mask with every pixel of `shape` alive. */
function fullWord(shape: number, w: number): number {
  const rest = shapes()[shape].pixelCount - w * 32;
  return rest >= 32 ? 0xffffffff : ((1 << rest) - 1) >>> 0;
}

/**
 * The run's view of every rigid body in the world.
 *
 * Owns three things and keeps them apart on purpose:
 *
 *   `sites`    where the seed put crates. Re-derived, never saved.
 *   `records`  what the run did to them. Saved; empty until something happens.
 *   `live`     which of them are instantiated right now. Neither of the above.
 */
export class RunBodies {
  private readonly rigid: RigidBodies;
  private readonly cells: CellWorld;

  /**
   * chunkKey -> flat `[x, y, shape]` triples. Re-derived on every generation.
   *
   * A triple rather than a pair since #42's doors: a chunk can draw a crate and
   * a hatch, and the site *index* is the ledger's key, so which slot a shape
   * lands in has to be stable across regenerations. It is, because the
   * decorators are nested once at construction and `noteSite` replaces by
   * shape rather than appending.
   */
  private readonly sites = new Map<number, number[]>();
  private readonly records = new Map<number, Record_>();
  /** siteKey -> instantiated body, and back. A body is in both or in neither. */
  private readonly live = new Map<number, Live>();
  private readonly ofBody = new Map<number, number>();

  /**
   * The run's copy of `records`, flat. This array **is** `RunState.bodies` once
   * `bind` has run, so an autosave taken at any moment sees the current ledger
   * without anything having to remember to flush it. Re-encoded inside `sync`
   * on the ticks where a record actually changed, and not otherwise.
   */
  flat: number[] = [];
  private encodedDirty = false;

  /** Seeds refused because `MAX_BODIES` was full. Telemetry, and a smell. */
  seedsRefused = 0;

  /** Bodies instantiated and retired since the run began. Telemetry. */
  seeded = 0;
  retired = 0;

  constructor(rigid: RigidBodies, cells: CellWorld) {
    this.rigid = rigid;
    this.cells = cells;
    // Chained rather than assigned, so wiring the ticker to the same hook later
    // does not silently unhook the ledger — which would make broken crates come
    // back, i.e. exactly the bug this class exists to prevent.
    const prev = rigid.onBreak;
    rigid.onBreak = (bodyId, x, y) => {
      this.noteBroken(bodyId);
      if (prev !== null) prev(bodyId, x, y);
    };
  }

  // ------------------------------------------------------------ the ledger

  /**
   * `BodySiteSink`. Idempotent: a regenerated chunk reports the same pose.
   *
   * Replaces the entry for that *shape* rather than clearing the list, because
   * the two decorators report separately and there is no "chunk begins" signal
   * between them — the old clear-and-push made whichever ran second delete the
   * other's site. Index is position in the list, and position is stable because
   * the nesting order is.
   */
  noteSite(cx: number, cy: number, x: number, y: number, shape: number): void {
    const key = chunkPack(cx, cy);
    const list = this.sites.get(key);
    if (list === undefined) {
      this.sites.set(key, [x, y, shape]);
      return;
    }
    for (let i = 0; i * 3 < list.length; i++) {
      if (list[i * 3 + 2] !== shape) continue;
      list[i * 3] = x;
      list[i * 3 + 1] = y;
      return;
    }
    list.push(x, y, shape);
  }

  /**
   * Adopt a run: read its ledger, then write through to it from here on.
   *
   * `run.bodies` is replaced by this object's own array rather than copied, so
   * there is one ledger and not two that drift. Call after `createRunState` and
   * after a resume.
   */
  bind(run: { bodies?: number[] }): void {
    this.records.clear();
    this.decode(run.bodies ?? []);
    this.reencode();
    run.bodies = this.flat;
  }

  /** Drop everything. The world is being regenerated on a new seed. */
  clear(): void {
    this.sites.clear();
    this.records.clear();
    this.live.clear();
    this.ofBody.clear();
    this.flat.length = 0;
    this.seedsRefused = 0;
    this.seeded = 0;
    this.retired = 0;
  }

  get recordCount(): number {
    return this.records.size;
  }

  get liveCount(): number {
    return this.live.size;
  }

  /** Whether the run has written this site off. Test seam and debug overlay. */
  isGone(cx: number, cy: number, index = 0): boolean {
    return this.records.get(siteKey(cx, cy, index))?.gone === true;
  }

  /**
   * A body came apart. The site is written off for the rest of the run.
   *
   * This is the half of the promise that a re-derived placement cannot make on
   * its own: `crateSiteFor` will keep answering "there is a crate here" for as
   * long as the run lasts, because it is a pure function of the seed. `gone` is
   * the run saying no.
   */
  private noteBroken(bodyId: number): void {
    const key = this.ofBody.get(bodyId);
    if (key === undefined) return;
    const l = this.live.get(key) as Live;
    this.ofBody.delete(bodyId);
    this.live.delete(key);
    this.records.set(key, { cx: l.cx, cy: l.cy, index: l.index, gone: true, cap: null });
    this.encodedDirty = true;
  }

  // --------------------------------------------------------------- the tick

  /**
   * Bring the instantiated set in line with where the player is.
   *
   * Call once a tick, right after `CellWorld.updateActive` and before the rigid
   * solver's `lift` — so a body seeded this tick is placed by this tick's
   * `writeBack`, and a body retired this tick has its cells out of the grid
   * before `cellSim` runs over them.
   */
  sync(focusX: number, focusY: number, viewW: number, viewH: number): void {
    // Bracketed under `rigid` rather than under a key of its own, because the
    // work it causes is charged to `RigidBodies.workThisTick` and booked against
    // docs/01 §9's rigid-body line. A pass with a budget and no `begin`/`end` is
    // a budget nobody can observe (#75), and a pass under its own key would
    // split one line item across two rows of the F3 overlay.
    profiler.begin('rigid');
    const fcx = Math.floor(focusX) >> CHUNK_SHIFT;
    const fcy = Math.floor(focusY) >> CHUNK_SHIFT;
    const halfW = Math.ceil(viewW / 2 / CHUNK_SIZE);
    const halfH = Math.ceil(viewH / 2 / CHUNK_SIZE);

    this.retirePass(fcx, fcy, halfW, halfH);
    this.seedPass(fcx, fcy, halfW, halfH);

    if (this.encodedDirty) {
      this.reencode();
      this.encodedDirty = false;
    }
    profiler.end('rigid');
  }

  private retirePass(fcx: number, fcy: number, halfW: number, halfH: number): void {
    for (const [key, l] of this.live) {
      const resident = this.cells.peekChunk(l.cx, l.cy) !== null;
      const rx = Math.abs(l.cx - fcx) - halfW;
      const ry = Math.abs(l.cy - fcy) - halfH;
      const ring = Math.max(rx > ry ? rx : ry, 0);
      if (resident && ring <= BODY_RING) continue;

      // The one place `clearCells` is decided. A resident chunk still holds the
      // body's STATIC pixels and they have to come out; an evicted one does
      // not, and clearing would regenerate it just to punch a hole in it.
      const cap = this.rigid.capture(l.id);
      this.rigid.retire(l.id, resident);
      this.live.delete(key);
      this.ofBody.delete(l.id);
      this.retired++;
      if (cap !== null) this.remember(key, l.cx, l.cy, l.index, cap);
    }
  }

  private seedPass(fcx: number, fcy: number, halfW: number, halfH: number): void {
    let budget = MAX_SEEDS_PER_TICK;
    // Row-major over the ring rather than over `sites`, so the order a chunk is
    // considered in depends on where the player is and not on the order chunks
    // happened to be generated. Two runs that reach the same place the same way
    // instantiate in the same order and get the same body ids.
    for (let cy = fcy - halfH - BODY_RING; cy <= fcy + halfH + BODY_RING && budget > 0; cy++) {
      for (let cx = fcx - halfW - BODY_RING; cx <= fcx + halfW + BODY_RING && budget > 0; cx++) {
        const list = this.sites.get(chunkPack(cx, cy));
        if (list === undefined) continue;
        for (let i = 0; i * 3 < list.length && budget > 0; i++) {
          const key = siteKey(cx, cy, i);
          if (this.live.has(key)) continue;
          const rec = this.records.get(key);
          if (rec !== undefined && rec.gone) continue;

          const sx = list[i * 3];
          const sy = list[i * 3 + 1];
          const shape = list[i * 3 + 2];
          // The segment is a fact about the carved doorway, so it comes from
          // the site every time — never from the record. A saved axis would be
          // a second statement of geometry the seed already makes, and the two
          // would disagree the first time the constant moved.
          const door = shape === SHAPE_DOOR ? doorAxisAt(sx, sy) : null;
          if (rec !== undefined && rec.cap !== null) {
            // `decode` cannot know a record's shape — the save does not carry
            // it, deliberately, because the site does. Filling it in here is
            // what keeps the flat format the same width it was before doors
            // existed, so a #84 save still decodes.
            rec.cap.shape = shape;
          }
          const id =
            rec !== undefined && rec.cap !== null
              ? this.rigid.restore(rec.cap, door)
              : door !== null
                ? this.rigid.spawnDoor(
                    shape, sx, sy, door.dx, door.dy, door.travel, door.senseDx, door.senseDy,
                  )
                : this.rigid.spawn(shape, sx, sy, false, 0);
          if (id < 0) {
            if (this.rigid.bodyCount >= MAX_BODIES) {
              // The registry is full. Try again when something retires — the
              // site keeps its record, and nothing is written off.
              this.seedsRefused++;
              continue;
            }
            // Not the registry, so the record describes a body that cannot
            // stand up: a liveness mask below `FAIL_FRACTION`, which `restore`
            // refuses because `step` would break it apart on its first tick and
            // drop a pile of planks into a chunk the player has just walked
            // into. Written off rather than retried — otherwise it costs a
            // `restore` attempt every tick for the rest of the run, and a save
            // hand-edited to hold one would quietly become a per-frame cost.
            this.records.set(key, { cx, cy, index: i, gone: true, cap: null });
            this.encodedDirty = true;
            continue;
          }
          this.live.set(key, { id, cx, cy, index: i });
          this.ofBody.set(id, key);
          this.seeded++;
          budget--;
        }
      }
    }
  }

  /**
   * Fold a captured body back into the ledger — or out of it.
   *
   * **A crate at its authored pose with every pixel intact is not recorded.**
   * That is the diff-log discipline docs/01 §8 sets for terrain, applied here:
   * the seed already says where that crate is, so a record would be a second,
   * redundant statement of the same fact — and one that would grow the save by
   * a row for every chunk the player has ever walked through, whether or not
   * they touched anything in it. The ledger stays the size of what was done.
   */
  private remember(key: number, cx: number, cy: number, index: number, cap: BodyCapture): void {
    const list = this.sites.get(chunkPack(cx, cy));
    const ax = list !== undefined ? list[index * 3] : NaN;
    const ay = list !== undefined ? list[index * 3 + 1] : NaN;
    let pristine = cap.x === ax && cap.y === ay && cap.orient === 0;
    if (pristine) {
      for (let w = 0; w < cap.alive.length && pristine; w++) {
        // Bits above `pixelCount` are never set by `capture`, so a full body's
        // last word is a partial mask rather than 0xffffffff — comparing
        // against the body's own count is the only version of this that works.
        pristine = cap.alive[w] === fullWord(cap.shape, w);
      }
    }
    // No record, and no `delete` on the way out either: nothing that produces
    // a record is reversible — a pixel does not grow back and a crate does not
    // roll home — so a pristine body has never had one. If that ever stops
    // being true this has to start removing them.
    if (pristine) return;
    this.records.set(key, { cx, cy, index, gone: false, cap });
    this.encodedDirty = true;
  }

  // -------------------------------------------------------------- encoding

  /**
   * `[cx, cy, index, gone, x, y, orient, nWords, ...words]` per record.
   *
   * Length-prefixed rather than fixed-width because the mask is `pixelCount`
   * bits and the crate is not the last shape that will exist — a doors-and-
   * minecarts ledger has to decode a crate written by an older build without a
   * version of its own. Flat numbers, so `serializeRun`'s `...rest` spread
   * carries it and `save.ts` needs no change.
   */
  private reencode(): void {
    const out = this.flat;
    out.length = 0;
    for (const r of this.records.values()) {
      out.push(r.cx, r.cy, r.index, r.gone ? 1 : 0);
      if (r.cap === null) out.push(0, 0, 0, 0);
      else {
        out.push(r.cap.x, r.cap.y, r.cap.orient, r.cap.alive.length);
        for (let w = 0; w < r.cap.alive.length; w++) out.push(r.cap.alive[w]);
      }
    }
  }

  private decode(flat: readonly number[]): void {
    let i = 0;
    while (i + REC_HEADER <= flat.length) {
      const cx = flat[i];
      const cy = flat[i + 1];
      const index = flat[i + 2];
      const gone = flat[i + 3] === 1;
      const x = flat[i + 4];
      const y = flat[i + 5];
      const orient = flat[i + 6];
      const n = flat[i + 7];
      i += REC_HEADER;
      // A truncated or hand-edited payload stops the walk rather than throwing.
      // The same rule `MIGRATIONS` follows: a save that will not decode costs
      // the player the profile, and defaulting is always the better answer.
      if (n < 0 || i + n > flat.length) return;
      const alive = new Uint32Array(n);
      for (let w = 0; w < n; w++) alive[w] = flat[i + w] >>> 0;
      i += n;
      this.records.set(siteKey(cx, cy, index), {
        cx,
        cy,
        index,
        gone,
        // `-1` is "ask the site", and it is never handed to the solver: the
        // only reader is `seedPass`, which overwrites it from the site's own
        // shape before restoring. The save records what the player did, and
        // which shape stands at a site is not that — it is seed geometry, and
        // storing it would be the redundant second statement this class's
        // header spends a paragraph refusing to make.
        cap: gone ? null : { shape: -1, x, y, orient, alive },
      });
    }
  }
}
